import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Converts a string into the same handle format Shopify's Liquid `handleize` filter
 * produces (lowercase, non-alphanumeric runs collapsed to a single hyphen, no leading/
 * trailing hyphens). Used to match a variant option's displayed value against the
 * `-{handle}-` token convention already used server-side in
 * snippets/product-media-gallery-content.liquid to group media by filename.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function handleize(value) {
  return (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    this.#syncMediaGroupSelection(newMediaGallery);
    this.replaceWith(newMediaGallery);
  };

  /**
   * The server picks which media group (e.g. a Fit value like Adult/Kids) to show based
   * on `selected_or_first_available_variant`, which silently falls back to an unrelated
   * variant when the currently selected option combination doesn't resolve to a real
   * variant (for example Fit: Kids while Size is still left on an Adult-only value).
   * When that happens, the fetched gallery's media group can disagree with what the
   * shopper actually has selected. This re-derives the intended group directly from the
   * variant picker's checked inputs (the source of truth for what the shopper clicked)
   * and corrects which slides are hidden in the fetched gallery before it's inserted
   * into the page. It never touches variant/price/availability resolution, and it's a
   * no-op for any product without a recognized grouping option, or when the server's
   * resolved group already agrees with the picker (the normal, valid-combination case).
   * @param {Element} newMediaGallery
   */
  #syncMediaGroupSelection(newMediaGallery) {
    const groupOptionHandle = newMediaGallery.dataset.mediaGroupOption;
    if (!groupOptionHandle) return;

    const pickerRoot = this.closest('.shopify-section, dialog') || document;
    const targetValue = this.#findCheckedGroupValue(pickerRoot, groupOptionHandle);
    if (targetValue == null) return;

    let targetHandle = handleize(targetValue);
    const fitOption = newMediaGallery.dataset.mediaFitOption;
    if (fitOption) {
      const fitValue = this.#findCheckedGroupValue(pickerRoot, fitOption);
      if (fitValue == null) return;
      targetHandle += `-${handleize(fitValue)}`;
    }
    if (!targetHandle || targetHandle === newMediaGallery.dataset.mediaGroup) return;

    const slides = newMediaGallery.querySelectorAll('slideshow-slide[data-media-group]');
    if (slides.length === 0) return;

    let matchCount = 0;
    slides.forEach((slide) => {
      if (slide.dataset.mediaGroup && slide.dataset.mediaGroup === targetHandle) matchCount++;
    });
    // Safety net: only override the server's grouping if the target group actually has
    // media to show. Otherwise leave the server-rendered result alone rather than
    // hiding everything.
    if (matchCount === 0) return;

    slides.forEach((slide) => {
      const slideGroup = slide.dataset.mediaGroup;
      if (!slideGroup) return; // ungrouped media always stays visible, same as server-side rule
      if (slideGroup === targetHandle) {
        slide.removeAttribute('hidden');
      } else {
        slide.setAttribute('hidden', '');
      }
    });
    newMediaGallery.dataset.mediaGroup = targetHandle;
  }

  /**
   * Finds the shopper's currently checked value for whichever variant-picker option
   * matches the given grouping option handle (e.g. "fit"), reading straight from the
   * picker's radio/select inputs rather than from the (possibly stale) resolved variant.
   * @param {ParentNode} root
   * @param {string} groupOptionHandle
   * @returns {string | null}
   */
  #findCheckedGroupValue(root, groupOptionHandle) {
    const fieldsets = root.querySelectorAll('variant-picker fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (!legend) continue;
      const legendClone = legend.cloneNode(true);
      legendClone.querySelectorAll('.variant-option__swatch-value').forEach((el) => el.remove());
      const optionName = legendClone.textContent?.trim();
      if (handleize(optionName) !== groupOptionHandle) continue;
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) return checked.value;
    }

    const selects = root.querySelectorAll('variant-picker select[name^="options["]');
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const match = /^options\[(.+)\]$/.exec(select.name);
      const optionName = match?.[1];
      if (!optionName || handleize(optionName) !== groupOptionHandle) continue;
      return select.options[select.selectedIndex]?.value ?? null;
    }

    return null;
  }

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

